import { Injectable, inject } from '@angular/core';
import { from, map, Observable } from 'rxjs';
import {
  PoliciesApi,
  PolicyConstraintAttributeKeyEnum,
  PolicyConstraint,
  PolicyConstraintOperator,
  PolicyCreateRequest,
  PolicyCreateRequestActionEnum,
  PolicyDetails,
  PolicyDetailsActionEnum,
  PolicyRule,
  PolicyRuleType,
  PolicyUpdateRequest,
  PolicyUpdateRequestActionEnum,
  PolicyValue,
  PolicyValueType
} from '@adesso/dataspace-api-client';
import { ADESSO_API_CONFIG } from '../../../../core/api/adesso-api.config';
import type { Constraint } from '../models/constraint.model';
import type { Policy, PolicyRole } from '../models/policy.model';
import { PolicyType } from '../models/policy.model';
import { PolicyApiPort } from '../ports/policy-api.port';

@Injectable()
export class AdessoPolicyApiAdapter extends PolicyApiPort {
  private readonly api = new PoliciesApi(inject(ADESSO_API_CONFIG));

  getPolicies(): Observable<Policy[]> {
    return from(this.api.getAllPolicies()).pipe(
      map((res) => (res.data ?? []).map(toDomain))
    );
  }

  sendPolicy(policy: Policy, role?: PolicyRole): Observable<{ policyId: string }> {
    return from(this.api.createPolicy(toCreateRequest(policy, role))).pipe(
      map((res) => ({ policyId: res.data.id ?? policy.id ?? '' }))
    );
  }

  updatePolicy(id: string, policy: Policy, role?: PolicyRole): Observable<void> {
    return from(this.api.updatePolicy(id, toUpdateRequest(policy, role))).pipe(map(() => undefined));
  }

  deletePolicy(id: string): Observable<void> {
    return from(this.api.removePolicy(id)).pipe(map(() => undefined));
  }
}

function toDomain(dto: PolicyDetails): Policy {
  const constraints = dto.rule ? flattenPolicyRule(dto.rule) : [];
  const privateProps = (dto.privateProperties ?? {}) as Record<string, unknown>;
  return {
    id: dto.policyId ?? '',
    createdAt: dto.createdAt ?? 0,
    constraints,
    prohibition: [],
    obligation: [],
    role: dto.action === PolicyDetailsActionEnum.ACCESS ? 'access' : 'usage',
    privateProperties: {
      name: str(privateProps['name']),
      description: str(privateProps['description'])
    }
  };
}

function toCreateRequest(policy: Policy, role?: PolicyRole): PolicyCreateRequest {
  const effectiveRole = role ?? policy.role;
  return {
    policyId: policy.id,
    rule: buildPolicyRule(policy.constraints ?? []),
    action: effectiveRole === 'access'
      ? PolicyCreateRequestActionEnum.ACCESS
      : PolicyCreateRequestActionEnum.USE,
    privateProperties: privatePropertiesToBag(policy.privateProperties)
  };
}

function toUpdateRequest(policy: Policy, role?: PolicyRole): PolicyUpdateRequest {
  const effectiveRole = role ?? policy.role;
  return {
    rule: buildPolicyRule(policy.constraints ?? []),
    action: effectiveRole === 'access'
      ? PolicyUpdateRequestActionEnum.ACCESS
      : PolicyUpdateRequestActionEnum.USE,
    privateProperties: privatePropertiesToBag(policy.privateProperties)
  };
}

function privatePropertiesToBag(
  pp: Policy['privateProperties']
): Record<string, object> | undefined {
  if (!pp) return undefined;
  const bag: Record<string, object> = {};
  if (pp.name) bag['name'] = pp.name as unknown as object;
  if (pp.description) bag['description'] = pp.description as unknown as object;
  return Object.keys(bag).length === 0 ? undefined : bag;
}

function buildPolicyRule(constraints: Constraint[]): PolicyRule {
  const operands = constraints
    .map(constraintToRule)
    .filter((r): r is PolicyRule => r != null);

  if (operands.length === 0) return { ruleType: PolicyRuleType.EMPTY };
  if (operands.length === 1) return operands[0];
  return { ruleType: PolicyRuleType.AND, operands };
}

function constraintToRule(c: Constraint): PolicyRule | null {
  const pc = constraintToPolicyConstraint(c);
  return pc ? { ruleType: PolicyRuleType.CONSTRAINT, constraint: pc } : null;
}

function constraintToPolicyConstraint(c: Constraint): PolicyConstraint | null {
  switch (c.type) {
    case PolicyType.ALWAYS_TRUE:
      return null;

    case PolicyType.PARTICIPANT_RESTRICTED:
      if (!c.bpnNumbers || c.bpnNumbers.length === 0) return null;
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.BUSINESS_PARTNER_NUMBER,
        operator: PolicyConstraintOperator.IS_ANY_OF,
        value: {
          valueType: PolicyValueType.TEXT_LIST,
          textList: [...c.bpnNumbers]
        }
      };

    case PolicyType.TIME_PERIOD_RESTRICTED:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.DATA_USAGE_END_DURATION_DAYS,
        operator: PolicyConstraintOperator.EQ,
        value: textValue(String(c.count))
      };

    case PolicyType.MEMBERSHIP_STATIC:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.MEMBERSHIP,
        operator: PolicyConstraintOperator.EQ,
        value: textValue('active')
      };

    case PolicyType.FRAMEWORK_AGREEMENT:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.FRAMEWORK_AGREEMENT,
        operator: PolicyConstraintOperator.EQ,
        value: textValue('DataExchangeGovernance')
      };

    case PolicyType.USE_CASE_MEMBERSHIP:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.USAGE_PURPOSE,
        operator: PolicyConstraintOperator.EQ,
        value: textValue(c.useCaseId)
      };

    case PolicyType.END_DATE:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.DATA_USAGE_END_DATE,
        operator: PolicyConstraintOperator.EQ,
        value: textValue(c.endDate.toISOString())
      };

    case PolicyType.COMPANY_REGION_RESTRICTED:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.AFFILIATES_REGION,
        operator: PolicyConstraintOperator.EQ,
        value: textValue(c.region)
      };

    case PolicyType.DATACENTER_REGION_RESTRICTED:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.JURISDICTION_LOCATION,
        operator: PolicyConstraintOperator.EQ,
        value: textValue(c.region)
      };

    case PolicyType.UNKNOWN:
      return {
        attributeKey: PolicyConstraintAttributeKeyEnum.UNKNOWN,
        attributeUri: c.leftOperand,
        operator: PolicyConstraintOperator.EQ,
        value: textValue(c.rightOperand)
      };

    default:
      return null;
  }
}

function textValue(text: string): PolicyValue {
  return { valueType: PolicyValueType.TEXT, text };
}

function flattenPolicyRule(rule: PolicyRule): Constraint[] {
  switch (rule.ruleType) {
    case PolicyRuleType.CONSTRAINT:
      return rule.constraint ? [mapTypedConstraint(rule.constraint)] : [];
    case PolicyRuleType.AND:
    case PolicyRuleType.OR:
    case PolicyRuleType.XONE:
      return (rule.operands ?? []).flatMap(flattenPolicyRule);
    case PolicyRuleType.EMPTY:
    default:
      return [];
  }
}

function mapTypedConstraint(c: PolicyConstraint): Constraint {
  const value = c.value;

  switch (c.attributeKey) {
    case PolicyConstraintAttributeKeyEnum.BUSINESS_PARTNER_NUMBER:
      return {
        type: PolicyType.PARTICIPANT_RESTRICTED,
        bpnNumbers: extractBpnList(value, c.operator)
      };

    case PolicyConstraintAttributeKeyEnum.DATA_USAGE_END_DURATION_DAYS:
      return {
        type: PolicyType.TIME_PERIOD_RESTRICTED,
        count: Number(extractText(value)) || 0
      };

    case PolicyConstraintAttributeKeyEnum.MEMBERSHIP:
      return { type: PolicyType.MEMBERSHIP_STATIC };

    case PolicyConstraintAttributeKeyEnum.FRAMEWORK_AGREEMENT:
      return { type: PolicyType.FRAMEWORK_AGREEMENT };

    case PolicyConstraintAttributeKeyEnum.USAGE_PURPOSE:
      return {
        type: PolicyType.USE_CASE_MEMBERSHIP,
        useCaseId: extractText(value) as 'UC.digitalization.sc' | 'UC.shortage.sc' | 'UC.pcf'
      };

    case PolicyConstraintAttributeKeyEnum.DATA_USAGE_END_DATE:
      return {
        type: PolicyType.END_DATE,
        endDate: new Date(extractText(value))
      };

    case PolicyConstraintAttributeKeyEnum.AFFILIATES_REGION:
      return {
        type: PolicyType.COMPANY_REGION_RESTRICTED,
        region: extractText(value) as 'Europe'
      };

    case PolicyConstraintAttributeKeyEnum.JURISDICTION_LOCATION:
      return {
        type: PolicyType.DATACENTER_REGION_RESTRICTED,
        region: extractText(value) as 'Europe'
      };

    case PolicyConstraintAttributeKeyEnum.UNKNOWN:
    default:
      return {
        type: PolicyType.UNKNOWN,
        leftOperand: c.attributeUri ?? '',
        rightOperand: extractText(value)
      };
  }
}

function extractText(value: PolicyValue | undefined): string {
  if (!value) return '';
  if (value.valueType === PolicyValueType.TEXT_LIST) {
    return (value.textList ?? []).join(',');
  }
  return value.text ?? '';
}

function extractBpnList(
  value: PolicyValue | undefined,
  operator: PolicyConstraintOperator
): string[] {
  if (!value) return [];
  if (value.valueType === PolicyValueType.TEXT_LIST) {
    return (value.textList ?? []).map((s) => s.trim()).filter(Boolean);
  }
  const raw = value.text ?? '';
  if (operator === PolicyConstraintOperator.IS_ANY_OF || operator === PolicyConstraintOperator.IN) {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return raw ? [raw] : [];
}

function str(v: unknown): string {
  return v == null ? '' : String(v);
}
